// Port of webapp/src/components/kanban/kanban.tsx
//
// Layout note: upstream splits the sticky header row (.octo-board-header) from
// the body row (.octo-board-body) so each column scrolls independently. This
// clone keeps one flex item per column with its header inside; the measured
// 260px + 15px gutter is the same, the whole board area scrolls.

import {ReactElement, useMemo, useState} from 'react'
import {Board, BoardGroup, IPropertyOption} from '../../types/board'
import {BoardView} from '../../types/boardView'
import {Card} from '../../types/card'
import {IUser} from '../../types/user'
import {Utils, IDType} from '../../common/utils'
import * as mutator from '../../state/mutator'
import {Icon} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem} from '../common/menu'
import KanbanColumn from './kanbanColumn'

type Props = {
    board: Board
    view: BoardView
    groups: BoardGroup[]
    hiddenGroups: BoardGroup[]
    usersById: Record<string, IUser>
    readOnly: boolean
    onCardClicked: (cardId: string) => void
}

export default function Kanban({board, view, groups, hiddenGroups, usersById, readOnly, onCardClicked}: Props): ReactElement {
    const [draggingCard, setDraggingCard] = useState<Card | null>(null)

    const groupByProperty = useMemo(
        () => (view.fields.groupById ? board.cardProperties.find((p) => p.id === view.fields.groupById) : undefined),
        [board.cardProperties, view.fields.groupById],
    )

    // the grouping property is represented by the column itself, so cards don't repeat it
    const activeProps = useMemo(() => board.cardProperties.filter((p) => {
        if (groupByProperty && p.id === groupByProperty.id) {
            return false
        }
        return view.fields.visiblePropertyIds.length === 0 || view.fields.visiblePropertyIds.includes(p.id)
    }), [board.cardProperties, groupByProperty, view.fields.visiblePropertyIds])

    const collapsed = useMemo(() => new Set(view.fields.collapsedOptionIds), [view.fields.collapsedOptionIds])

    const dropOnColumn = async (optionId: string, targetCard: Card | undefined, draggedId: string): Promise<void> => {
        const pool = groups.flatMap((g) => g.cards)
        const dragged = draggingCard || pool.find((c) => c.id === draggedId)
        setDraggingCard(null)
        if (!dragged || readOnly) {
            return
        }
        await mutator.moveCard(board.id, view, dragged, optionId, targetCard)
    }

    const addCard = async (optionId: string): Promise<void> => {
        const properties: Record<string, string> = {}
        if (groupByProperty) {
            properties[groupByProperty.id] = optionId
        }
        const card = await mutator.createCard(board, view, {properties, title: '未命名'})
        if (optionId === '' && groupByProperty) {
            // an empty string means "no value"; drop the property entirely
            await mutator.changeCardFieldValue(board.id, card, groupByProperty.id, undefined)
        }
        onCardClicked(card.id)
    }

    const toggleCollapsed = (optionId: string): void => {
        const next = collapsed.has(optionId) ?
            [...collapsed].filter((id) => id !== optionId) :
            [...collapsed, optionId]
        void mutator.changeViewCollapsedOptions(board.id, view, next)
    }

    const hideGroup = (option: IPropertyOption): void => {
        void Promise.all([
            mutator.changeViewVisibleOptionIds(board.id, view, view.fields.visibleOptionIds.filter((id) => id !== option.id)),
            mutator.changeViewHiddenOptionIds(board.id, view, [...view.fields.hiddenOptionIds, option.id]),
        ])
    }

    const showGroup = (option: IPropertyOption): void => {
        void Promise.all([
            mutator.changeViewHiddenOptionIds(board.id, view, view.fields.hiddenOptionIds.filter((id) => id !== option.id)),
            mutator.changeViewVisibleOptionIds(board.id, view, [...view.fields.visibleOptionIds, option.id]),
        ])
    }

    const renameOption = (option: IPropertyOption, value: string): void => {
        if (groupByProperty) {
            void mutator.changePropertyOption(board, groupByProperty, {...option, value})
        }
    }

    const recolorOption = (option: IPropertyOption, color: string): void => {
        if (groupByProperty) {
            void mutator.changePropertyOption(board, groupByProperty, {...option, color})
        }
    }

    const addOption = (): void => {
        if (!groupByProperty) {
            return
        }
        const option: IPropertyOption = {
            id: Utils.createGuid(IDType.BlockID),
            value: '新建',
            color: 'propColorDefault',
        }
        void mutator.insertPropertyOption(board, groupByProperty, option)
    }

    return (
        <div className='Kanban flex-1 overflow-auto'>
            <div className='flex flex-row items-start pb-10'>
                {groups.map((group) => (
                    <KanbanColumn
                        key={group.option.id || 'blank'}
                        group={group}
                        groupByProperty={groupByProperty}
                        activeProps={activeProps}
                        usersById={usersById}
                        isCollapsed={collapsed.has(group.option.id)}
                        readOnly={readOnly}
                        draggingCardId={draggingCard?.id ?? null}
                        onCardClicked={onCardClicked}
                        onToggleCollapsed={toggleCollapsed}
                        onAddCard={addCard}
                        onDragStart={setDraggingCard}
                        onDragEnd={() => setDraggingCard(null)}
                        onDropOnColumn={dropOnColumn}
                        onTitleChange={renameOption}
                        onColorChange={recolorOption}
                        onHideGroup={hideGroup}
                        onAddOption={addOption}
                    />
                ))}

                {hiddenGroups.length > 0 && !readOnly && (
                    <div className='octo-board-column'>
                        <ButtonWithMenu
                            button={
                                <span className='flex cursor-pointer items-center gap-2 py-2 opacity-70'>
                                    <Icon name='eye-off'/>
                                    <span className='text-sm font-semibold'>{hiddenGroups.length} hidden group{hiddenGroups.length > 1 ? 's' : ''}</span>
                                </span>
                            }
                        >
                            <Menu ariaLabel='已隐藏的分组'>
                                {hiddenGroups.map((group) => (
                                    <MenuItem
                                        key={group.option.id || 'blank'}
                                        icon='eye'
                                        onClick={() => showGroup(group.option)}
                                    >
                                        {group.option.value || `未设置${groupByProperty?.name}`} ({group.cards.length})
                                    </MenuItem>
                                ))}
                                <MenuItem
                                    icon='eye'
                                    onClick={() => mutator.changeViewHiddenOptionIds(board.id, view, [])}
                                >
                                    显示全部分组
                                </MenuItem>
                            </Menu>
                        </ButtonWithMenu>
                    </div>
                )}
            </div>
        </div>
    )
}
